import { useEffect, useState, useCallback, useMemo, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsers, createUser, updateUser, deleteUser } from "@/state/users/users.slice";
import { fetchRoles } from "@/state/roles/roles.slice";
import { getShops } from "@/state/shops/shop.slice";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, MagnifyingGlass, Users } from "@phosphor-icons/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

// Lazy load components for code splitting
const UserFormDialog = lazy(() => import("@/components/custom/users/UserFormDialog"));
const UsersTable = lazy(() => import("@/components/custom/users/UsersTable"));

export default function UserManagement() {
  const dispatch = useDispatch();
  const { toast } = useToast();

  // Redux state
  const { users, isLoading } = useSelector((state) => state.users);
  const { roles } = useSelector((state) => state.roles);
  const { shops } = useSelector((state) => state.shop || { shops: [] });
  const currentUser = useSelector((state) => state.auth.user);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchRoles());
    dispatch(getShops());
  }, [dispatch]);

  // Filtered users based on search
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    const search = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        user.username?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search) ||
        user.first_name?.toLowerCase().includes(search) ||
        user.last_name?.toLowerCase().includes(search)
    );
  }, [users, searchTerm]);

  // Stats
  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.status === 1).length,
    inactive: users.filter((u) => u.status !== 1).length,
  }), [users]);

  // Handlers
  const handleAddUser = useCallback(() => {
    setEditingUser(null);
    setIsFormOpen(true);
  }, []);

  const handleEditUser = useCallback((user) => {
    setEditingUser(user);
    setIsFormOpen(true);
  }, []);

  const handleDeleteClick = useCallback((user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!userToDelete) return;

    try {
      await dispatch(deleteUser(userToDelete.id)).unwrap();
      toast({ title: "Success", description: "User deleted successfully" });
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error });
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  }, [dispatch, userToDelete, toast]);

  const handleFormSubmit = useCallback(async (formData, userId) => {
    try {
      if (userId) {
        await dispatch(updateUser({ id: userId, userData: formData })).unwrap();
        toast({ title: "Success", description: "User updated successfully" });
      } else {
        await dispatch(createUser(formData)).unwrap();
        toast({ title: "Success", description: "User created successfully" });
      }
      setIsFormOpen(false);
      setEditingUser(null);
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error });
    }
  }, [dispatch, toast]);

  return (
    <div className="mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="h-8 w-8" />
            User Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage system users and their access permissions
          </p>
        </div>
        <Button onClick={handleAddUser} className="gap-2">
          <Plus className="h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Users</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active Users</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">{stats.active}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Inactive Users</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-gray-500 dark:text-gray-400">{stats.inactive}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Users Table */}
      <Suspense fallback={<div className="h-64 flex items-center justify-center">Loading...</div>}>
        <UsersTable
          users={filteredUsers}
          isLoading={isLoading}
          onEdit={handleEditUser}
          onDelete={handleDeleteClick}
        />
      </Suspense>

      {/* Form Dialog */}
      <Suspense fallback={null}>
        <UserFormDialog
          open={isFormOpen}
          onOpenChange={setIsFormOpen}
          user={editingUser}
          roles={roles}
          shops={shops}
          onSubmit={handleFormSubmit}
          isLoading={isLoading}
        />
      </Suspense>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong>{userToDelete?.username}</strong>? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
